import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import ToggleInput from "@components/inputs/ToggleInput";
import DataPill from "@components/pills/DataPill";
import { userManagementApis } from "@services/apis/UserManagement";
import {
  DefaultConfigResponse,
  PutConfigRequest,
  ServiceConfig,
} from "@services/models/UserManagement/get-services";
import { useEffect, useState } from "react";
import UserManagementClasses from "../UserComponents";

interface CPGConfigContainerProps {
  userId?: number;
  cpgData?: ServiceConfig;
  fullConfig: DefaultConfigResponse;
}

const CPGConfigContainer: React.FC<CPGConfigContainerProps> = ({
  userId,
  cpgData,
  fullConfig,
}) => {
  const userManegementClasses = new UserManagementClasses();
  const [isDirty, setIsDirty] = useState(false);
  const [payInBps, setPayInBps] = useState<number>(0);
  const [payOutBps, setPayOutBps] = useState<number>(0);
  const [minPayInFees, setMinPayInFees] = useState<number>(0);
  const [minPayOutFees, setMinPayOutFees] = useState<number>(0);
  const [isPayInEnabled, setIsPayInEnabled] = useState<boolean>(false);
  const [isPayOutEnabled, setIsPayOutEnabled] = useState<boolean>(false);
  const [payInPerTransactionMaxAmount, setPayInPerTransactionMaxAmount] =
    useState<number>(0);
  const [payInPerTransactionMinAmount, setPayInPerTransactionMinAmount] =
    useState<number>(0);
  const [payOutPerTransactionMaxAmount, setPayOutPerTransactionMaxAmount] =
    useState<number>(0);
  const [payOutPerTransactionMinAmount, setPayOutPerTransactionMinAmount] =
    useState<number>(0);

  const [payInDailyMaxAmount, setPayInDailyMaxAmount] = useState<number>(0);
  const [payInDailyMaxTransactions, setPayInDailyMaxTransactions] =
    useState<number>(0);
  const [payOutDailyMaxAmount, setPayOutDailyMaxAmount] = useState<number>(0);
  const [payOutDailyMaxTransactions, setPayOutDailyMaxTransactions] =
    useState<number>(0);

  const [payInMonthlyMaxAmount, setPayInMonthlyMaxAmount] = useState<number>(0);
  const [payInMonthlyMaxTransactions, setPayInMonthlyMaxTransactions] =
    useState<number>(0);
  const [payOutMonthlyMaxAmount, setPayOutMonthlyMaxAmount] =
    useState<number>(0);
  const [payOutMonthlyMaxTransactions, setPayOutMonthlyMaxTransactions] =
    useState<number>(0);

  const [payInUSDTEnabled, setPayInUSDTEnabled] = useState<boolean>(false);
  const [payOutUSDTEnabled, setPayOutUSDTEnabled] = useState<boolean>(false);
  const [payInUSDCEnabled, setPayInUSDCEnabled] = useState<boolean>(false);
  const [payOutUSDCEnabled, setPayOutUSDCEnabled] = useState<boolean>(false);
  const [payInDAIEnabled, setPayInDAIEnabled] = useState<boolean>(false);
  const [payOutDAIEnabled, setPayOutDAIEnabled] = useState<boolean>(false);

  const [ethPayInNetworkEnabled, setEthNetworkEnabled] =
    useState<boolean>(false);
  const [polPayInNetworkEnabled, setPolNetworkEnabled] =
    useState<boolean>(false);
  const [maticPayInNetworkEnabled, setMaticNetworkEnabled] =
    useState<boolean>(false);
  const [bscPayInNetworkEnabled, setBscNetworkEnabled] =
    useState<boolean>(false);
  const [ethPayOutNetworkEnabled, setEthPayOutNetworkEnabled] =
    useState<boolean>(false);
  const [polPayOutNetworkEnabled, setPolPayOutNetworkEnabled] =
    useState<boolean>(false);
  const [maticPayOutNetworkEnabled, setMaticPayOutNetworkEnabled] =
    useState<boolean>(false);
  const [bscPayOutNetworkEnabled, setBscPayOutNetworkEnabled] =
    useState<boolean>(false);

  const handleInitialValues = () => {
    setPayInBps(cpgData?.fees?.payIn.payInBps || 0);
    setPayOutBps(cpgData?.fees?.payOut.payOutBps || 0);
    setMinPayInFees(cpgData?.fees?.payIn.minFeesFixed || 0);
    setMinPayOutFees(cpgData?.fees?.payOut.minFeesFixed || 0);
    setIsPayInEnabled(cpgData?.payInEnabled || false);
    setIsPayOutEnabled(cpgData?.payOutEnabled || false);
    setPayInPerTransactionMaxAmount(
      cpgData?.limits?.transactionLimits?.perTransactionLimit?.payIn
        .maxAmountAllowed || 0,
    );
    setPayInPerTransactionMinAmount(
      cpgData?.limits?.transactionLimits?.perTransactionLimit?.payIn
        .minAmountAllowed || 0,
    );
    setPayOutPerTransactionMaxAmount(
      cpgData?.limits?.transactionLimits?.perTransactionLimit?.payOut
        .maxAmountAllowed || 0,
    );
    setPayOutPerTransactionMinAmount(
      cpgData?.limits?.transactionLimits?.perTransactionLimit?.payOut
        .minAmountAllowed || 0,
    );
    setPayInDailyMaxAmount(
      cpgData?.limits?.transactionLimits?.dailyTransactionLimit?.payIn
        .maxAmountAllowed || 0,
    );
    setPayInDailyMaxTransactions(
      cpgData?.limits?.transactionLimits?.dailyTransactionLimit?.payIn
        .maxTransactionAllowed || 0,
    );
    setPayOutDailyMaxAmount(
      cpgData?.limits?.transactionLimits?.dailyTransactionLimit?.payOut
        .maxAmountAllowed || 0,
    );
    setPayOutDailyMaxTransactions(
      cpgData?.limits?.transactionLimits?.dailyTransactionLimit?.payOut
        .maxTransactionAllowed || 0,
    );
    setPayInMonthlyMaxAmount(
      cpgData?.limits?.transactionLimits?.monthlyTransactionLimit?.payIn
        .maxAmountAllowed || 0,
    );
    setPayInMonthlyMaxTransactions(
      cpgData?.limits?.transactionLimits?.monthlyTransactionLimit?.payIn
        .maxTransactionAllowed || 0,
    );
    setPayOutMonthlyMaxAmount(
      cpgData?.limits?.transactionLimits?.monthlyTransactionLimit?.payOut
        .maxAmountAllowed || 0,
    );
    setPayOutMonthlyMaxTransactions(
      cpgData?.limits?.transactionLimits?.monthlyTransactionLimit?.payOut
        .maxTransactionAllowed || 0,
    );
    setPayInUSDTEnabled(cpgData?.enabledCryptos?.payIn?.USDT || false);
    setPayOutUSDTEnabled(cpgData?.enabledCryptos?.payOut?.USDT || false);
    setPayInUSDCEnabled(cpgData?.enabledCryptos?.payIn?.USDC || false);
    setPayOutUSDCEnabled(cpgData?.enabledCryptos?.payOut?.USDC || false);
    setPayInDAIEnabled(cpgData?.enabledCryptos?.payIn?.DAI || false);
    setPayOutDAIEnabled(cpgData?.enabledCryptos?.payOut?.DAI || false);

    setEthNetworkEnabled(cpgData?.enabledNetworks?.payIn.ETH || false);
    setPolNetworkEnabled(cpgData?.enabledNetworks?.payIn.POL || false);
    setMaticNetworkEnabled(cpgData?.enabledNetworks?.payIn.MATIC || false);
    setBscNetworkEnabled(cpgData?.enabledNetworks?.payIn.BSC || false);
    setEthPayOutNetworkEnabled(cpgData?.enabledNetworks?.payOut.ETH || false);
    setPolPayOutNetworkEnabled(cpgData?.enabledNetworks?.payOut.POL || false);
    setMaticPayOutNetworkEnabled(
      cpgData?.enabledNetworks?.payOut.MATIC || false,
    );
    setBscPayOutNetworkEnabled(cpgData?.enabledNetworks?.payOut.BSC || false);
  };

  const handleUpdate = async () => {
    try {
      const updatedConfig: ServiceConfig = {
        enabled: cpgData?.enabled || false,

        payInEnabled: isPayInEnabled,
        payOutEnabled: isPayOutEnabled,

        stablecoinParity: cpgData?.stablecoinParity!,

        limits: {
          transactionLimits: {
            perTransactionLimit: {
              payIn: {
                minAmountAllowed: payInPerTransactionMinAmount,
                maxAmountAllowed: payInPerTransactionMaxAmount,
              },
              payOut: {
                minAmountAllowed: payOutPerTransactionMinAmount,
                maxAmountAllowed: payOutPerTransactionMaxAmount,
              },
            },

            dailyTransactionLimit: {
              payIn: {
                maxAmountAllowed: payInDailyMaxAmount,
                maxTransactionAllowed: payInDailyMaxTransactions,
              },
              payOut: {
                maxAmountAllowed: payOutDailyMaxAmount,
                maxTransactionAllowed: payOutDailyMaxTransactions,
              },
            },

            monthlyTransactionLimit: {
              payIn: {
                maxAmountAllowed: payInMonthlyMaxAmount,
                maxTransactionAllowed: payInMonthlyMaxTransactions,
              },
              payOut: {
                maxAmountAllowed: payOutMonthlyMaxAmount,
                maxTransactionAllowed: payOutMonthlyMaxTransactions,
              },
            },
          },
        },

        enabledCryptos: {
          payIn: {
            USDT: payInUSDTEnabled,
            USDC: payInUSDCEnabled,
            DAI: payInDAIEnabled,
          },

          payOut: {
            USDT: payOutUSDTEnabled,
            USDC: payOutUSDCEnabled,
            DAI: payOutDAIEnabled,
          },
        },

        enabledNetworks: {
          payIn: {
            ETH: ethPayInNetworkEnabled,
            POL: polPayInNetworkEnabled,
            MATIC: maticPayInNetworkEnabled,
            BSC: bscPayInNetworkEnabled,
          },

          payOut: {
            ETH: ethPayOutNetworkEnabled,
            POL: polPayOutNetworkEnabled,
            MATIC: maticPayOutNetworkEnabled,
            BSC: bscPayOutNetworkEnabled,
          },
        },

        fees: {
          payIn: {
            payInBps: payInBps,
            minFeesFixed: minPayInFees,
          },

          payOut: {
            payOutBps: payOutBps,
            minFeesFixed: minPayOutFees,
          },
        },
      };

      const request: PutConfigRequest = {
        userId: userId,
        config: {
          userAccountConfig: fullConfig?.userAccountConfig!,
          merchantAccountConfig: fullConfig?.merchantAccountConfig!,
          services: {
            CPG: updatedConfig,
            BRL_PAYIN: fullConfig?.services.BRL_PAYIN!,
          },
        },
      };

      await userManagementApis.updateUserConfig(request);

      console.log("Updated successfully");
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancel = () => {
    handleInitialValues();
  };

  const buildCurrentConfig = (): ServiceConfig => {
    return {
      enabled: cpgData?.enabled || false,

      payInEnabled: isPayInEnabled,
      payOutEnabled: isPayOutEnabled,

      stablecoinParity: cpgData?.stablecoinParity!,

      limits: {
        transactionLimits: {
          perTransactionLimit: {
            payIn: {
              minAmountAllowed: payInPerTransactionMinAmount,
              maxAmountAllowed: payInPerTransactionMaxAmount,
            },
            payOut: {
              minAmountAllowed: payOutPerTransactionMinAmount,
              maxAmountAllowed: payOutPerTransactionMaxAmount,
            },
          },

          dailyTransactionLimit: {
            payIn: {
              maxAmountAllowed: payInDailyMaxAmount,
              maxTransactionAllowed: payInDailyMaxTransactions,
            },

            payOut: {
              maxAmountAllowed: payOutDailyMaxAmount,
              maxTransactionAllowed: payOutDailyMaxTransactions,
            },
          },

          monthlyTransactionLimit: {
            payIn: {
              maxAmountAllowed: payInMonthlyMaxAmount,
              maxTransactionAllowed: payInMonthlyMaxTransactions,
            },

            payOut: {
              maxAmountAllowed: payOutMonthlyMaxAmount,
              maxTransactionAllowed: payOutMonthlyMaxTransactions,
            },
          },
        },
      },

      enabledCryptos: {
        payIn: {
          USDT: payInUSDTEnabled,
          USDC: payInUSDCEnabled,
          DAI: payInDAIEnabled,
        },

        payOut: {
          USDT: payOutUSDTEnabled,
          USDC: payOutUSDCEnabled,
          DAI: payOutDAIEnabled,
        },
      },

      enabledNetworks: {
        payIn: {
          ETH: ethPayInNetworkEnabled,
          POL: polPayInNetworkEnabled,
          MATIC: maticPayInNetworkEnabled,
          BSC: bscPayInNetworkEnabled,
        },

        payOut: {
          ETH: ethPayOutNetworkEnabled,
          POL: polPayOutNetworkEnabled,
          MATIC: maticPayOutNetworkEnabled,
          BSC: bscPayOutNetworkEnabled,
        },
      },

      fees: {
        payIn: {
          payInBps: payInBps,
          minFeesFixed: minPayInFees,
        },

        payOut: {
          payOutBps: payOutBps,
          minFeesFixed: minPayOutFees,
        },
      },
    };
  };

  useEffect(() => {
    if (!cpgData) return;

    setIsDirty(
      JSON.stringify(buildCurrentConfig()) !== JSON.stringify(cpgData),
    );
  }, [
    payInBps,
    payOutBps,
    minPayInFees,
    minPayOutFees,
    isPayInEnabled,
    isPayOutEnabled,
    payInPerTransactionMaxAmount,
    payInPerTransactionMinAmount,
    payOutPerTransactionMaxAmount,
    payOutPerTransactionMinAmount,
    payInDailyMaxAmount,
    payInDailyMaxTransactions,
    payOutDailyMaxAmount,
    payOutDailyMaxTransactions,
    payInMonthlyMaxAmount,
    payInMonthlyMaxTransactions,
    payOutMonthlyMaxAmount,
    payOutMonthlyMaxTransactions,
    payInUSDTEnabled,
    payOutUSDTEnabled,
    payInUSDCEnabled,
    payOutUSDCEnabled,
    payInDAIEnabled,
    payOutDAIEnabled,
    ethPayInNetworkEnabled,
    polPayInNetworkEnabled,
    maticPayInNetworkEnabled,
    bscPayInNetworkEnabled,
    ethPayOutNetworkEnabled,
    polPayOutNetworkEnabled,
    maticPayOutNetworkEnabled,
    bscPayOutNetworkEnabled,
    cpgData,
  ]);

  useEffect(() => {
    if (cpgData) {
      handleInitialValues();
    }
  }, [cpgData]);

  return (
    <div className="bg-bg-base flex flex-col p-[12px] gap-[16px]">
      {/* Basic */}
      <div className="flex flex-col">
        <span className="text-[12px] font-semibold pb-[8px]">BASIC</span>
        <div className="flex flex-col border border-border-subtle bg-bg-page-body rounded-[8px]">
          <div className="flex flex-row justify-between items-center p-[8px] border-b border-border-subtle">
            <span className="font-medium text-[10px]">Is PayIn Enabled?</span>
            <ToggleInput
              isToggled={isPayInEnabled}
              onToggle={() => setIsPayInEnabled(!isPayInEnabled)}
              size="sm"
            />
          </div>
          <div className="flex flex-row justify-between items-center p-[8px]">
            <span className="font-medium text-[10px]">Is PayOut Enabled?</span>
            <ToggleInput
              isToggled={isPayOutEnabled}
              onToggle={() => setIsPayOutEnabled(!isPayOutEnabled)}
              size="sm"
            />
          </div>
        </div>
      </div>

      {/* Fees */}
      <div className="flex flex-col">
        <span className="text-[12px] font-semibold pb-[8px]">FEES</span>
        <div className="rounded-[8px] border border-border-subtle bg-bg-page-body">
          <div className="flex flex-row p-[8px] border-b border-b-border-subtle">
            <span className="font-semibold text-[10px] w-[40%]">
              Description
            </span>
            <span className="font-semibold text-[10px] w-[30%]">PayIn</span>
            <span className="font-semibold text-[10px] w-[30%]">PayOut</span>
          </div>
          <div className="flex flex-row px-[8px] py-[4px] items-center border-b border-b-border-subtle">
            <span className="font-normal text-[10px] w-[40%]">Bps</span>
            <div className="w-[30%] mr-[8px]">
              <GlobalTextInputField
                type="number"
                value={payInBps.toString()}
                onChange={(e) => {
                  setPayInBps(Number(e.target.value));
                }}
                placeholder="Set new rate"
              />
            </div>
            <div className="w-[30%]">
              <GlobalTextInputField
                type="number"
                value={payOutBps.toString()}
                onChange={(e) => {
                  setPayOutBps(Number(e.target.value));
                }}
                placeholder="Set new rate"
              />
            </div>
          </div>
          <div className="flex flex-row px-[8px] py-[4px] justify-between items-center">
            <span className="font-normal text-[10px] w-[40%]">
              Min Fees Fixed
            </span>
            <div className="w-[30%] mr-[8px]">
              <GlobalTextInputField
                type="number"
                value={minPayInFees.toString()}
                onChange={(e) => {
                  setMinPayInFees(Number(e.target.value));
                }}
                placeholder="Set new rate"
              />
            </div>
            <div className="w-[30%]">
              <GlobalTextInputField
                type="number"
                value={minPayOutFees.toString()}
                onChange={(e) => {
                  setMinPayOutFees(Number(e.target.value));
                }}
                placeholder="Set new rate"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Limits */}
      <div className="flex flex-col gap-[8px]">
        <span className="text-[12px] font-semibold ">LIMITS</span>
        <div className="flex flex-col bg-bg-page-body border border-border-subtle rounded-[8px] gap-[4px]">
          <span className="text-[10px] font-medium text-text-label px-[8px] mt-[8px]">
            PER TRANSACTION
          </span>
          <div className="flex flex-row gap-[8px] px-[8px]">
            <div className="bg-bg-page-body  border border-border-subtle rounded-[8px] flex flex-col p-[8px] w-[50%]">
              <DataPill value="pay-in" label="PayIn" />
              <div className="flex flex-row gap-[8px]">
                <GlobalTextInputField
                  type="number"
                  label="Min amount"
                  prefixIcon="$"
                  prefixIconPadding="pl-[20px]"
                  value={payInPerTransactionMinAmount.toString()}
                  onChange={(e) => {
                    setPayInPerTransactionMinAmount(Number(e.target.value));
                  }}
                  placeholder="Set new rate"
                />
                <GlobalTextInputField
                  type="number"
                  label="Max amount"
                  prefixIcon="$"
                  prefixIconPadding="pl-[20px]"
                  value={payInPerTransactionMaxAmount.toString()}
                  onChange={(e) => {
                    setPayInPerTransactionMaxAmount(Number(e.target.value));
                  }}
                  placeholder="Set new rate"
                />
              </div>
            </div>
            <div className="bg-bg-page-body  border border-border-subtle rounded-[8px] flex flex-col p-[8px] w-[50%]">
              <DataPill value="pay-out" label="PayOut" />
              <div className="flex flex-row gap-[8px]">
                <GlobalTextInputField
                  type="number"
                  label="Min amount"
                  prefixIcon="$"
                  prefixIconPadding="pl-[20px]"
                  value={payOutPerTransactionMinAmount.toString()}
                  onChange={(e) => {
                    setPayOutPerTransactionMinAmount(Number(e.target.value));
                  }}
                  placeholder="Set new rate"
                />
                <GlobalTextInputField
                  type="number"
                  label="Max amount"
                  prefixIcon="$"
                  prefixIconPadding="pl-[20px]"
                  value={payOutPerTransactionMaxAmount.toString()}
                  onChange={(e) => {
                    setPayOutPerTransactionMaxAmount(Number(e.target.value));
                  }}
                  placeholder="Set new rate"
                />
              </div>
            </div>
          </div>
          <span className="text-[10px] font-medium text-text-label px-[8px]">
            DAILY LIMITS
          </span>
          <div className="flex flex-row gap-[8px] px-[8px]">
            <div className="bg-bg-page-body border border-border-subtle rounded-[8px] flex flex-col p-[8px] w-[50%]">
              <DataPill value="pay-in" label="PayIn" />
              <div className="flex flex-row gap-[8px]">
                <GlobalTextInputField
                  type="number"
                  label="Max amount"
                  prefixIcon="$"
                  prefixIconPadding="pl-[20px]"
                  value={payInDailyMaxAmount.toString()}
                  onChange={(e) => {
                    setPayInDailyMaxAmount(Number(e.target.value));
                  }}
                  placeholder="Set new rate"
                />
                <GlobalTextInputField
                  type="number"
                  label="Max transactions"
                  value={payInDailyMaxTransactions.toString()}
                  onChange={(e) => {
                    setPayInDailyMaxTransactions(Number(e.target.value));
                  }}
                  placeholder="Set new rate"
                />
              </div>
            </div>
            <div className="bg-bg-page-body  border border-border-subtle rounded-[8px] flex flex-col p-[8px] w-[50%]">
              <DataPill value="pay-out" label="PayOut" />
              <div className="flex flex-row gap-[8px]">
                <GlobalTextInputField
                  type="number"
                  label="Max amount"
                  prefixIcon="$"
                  prefixIconPadding="pl-[20px]"
                  value={payOutDailyMaxAmount.toString()}
                  onChange={(e) => {
                    setPayOutDailyMaxAmount(Number(e.target.value));
                  }}
                  placeholder="Set new rate"
                />
                <GlobalTextInputField
                  type="number"
                  label="Max transactions"
                  value={payOutDailyMaxTransactions.toString()}
                  onChange={(e) => {
                    setPayOutDailyMaxTransactions(Number(e.target.value));
                  }}
                  placeholder="Set new rate"
                />
              </div>
            </div>
          </div>
          <span className="text-[10px] font-medium text-text-label px-[8px]">
            MONTHLY LIMITS
          </span>
          <div className="flex flex-row gap-[8px] px-[8px]">
            <div className="bg-bg-page-body  border border-border-subtle rounded-[8px] flex flex-col p-[8px] w-[50%]">
              <DataPill value="pay-in" label="PayIn" />
              <div className="flex flex-row gap-[8px]">
                <GlobalTextInputField
                  type="number"
                  label="Max amount"
                  prefixIcon="$"
                  prefixIconPadding="pl-[20px]"
                  value={payInMonthlyMaxAmount.toString()}
                  onChange={(e) => {
                    setPayInMonthlyMaxAmount(Number(e.target.value));
                  }}
                  placeholder="Set new rate"
                />
                <GlobalTextInputField
                  type="number"
                  label="Max transactions"
                  value={payInMonthlyMaxTransactions.toString()}
                  onChange={(e) => {
                    setPayInMonthlyMaxTransactions(Number(e.target.value));
                  }}
                  placeholder="Set new rate"
                />
              </div>
            </div>
            <div className="bg-bg-page-body  border border-border-subtle rounded-[8px] flex flex-col p-[8px] w-[50%]">
              <DataPill value="pay-out" label="PayOut" />
              <div className="flex flex-row gap-[8px]">
                <GlobalTextInputField
                  type="number"
                  label="Max amount"
                  prefixIcon="$"
                  prefixIconPadding="pl-[20px]"
                  value={payOutMonthlyMaxAmount.toString()}
                  onChange={(e) => {
                    setPayOutMonthlyMaxAmount(Number(e.target.value));
                  }}
                  placeholder="Set new rate"
                />
                <GlobalTextInputField
                  type="number"
                  label="Max transactions"
                  value={payOutMonthlyMaxTransactions.toString()}
                  onChange={(e) => {
                    setPayOutMonthlyMaxTransactions(Number(e.target.value));
                  }}
                  placeholder="Set new rate"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Crypto */}
      <div className="flex flex-col">
        <span className="text-[12px] font-semibold pb-[8px]">CRYPTO</span>
        <div className="bg-bg-page-body border border-border-subtle rounded-[8px]">
          <div className="flex flex-row items-center border-b border-border-subtle py-[8px] px-[8px]">
            <span className="font-bold text-[10px] w-[33%] text-start">
              Currency
            </span>
            <span className="font-bold text-[10px] w-[33%] text-end">
              PayIn
            </span>
            <span className="font-bold text-[10px] w-[33%] text-end">
              PayOut
            </span>
          </div>
          <div className="flex flex-row justify-between items-center border-b border-border-subtle py-[8px] px-[8px] w">
            <span className="font-medium text-[10px] w-[33%]">USDT</span>
            <div className="w-[33%] flex justify-end">
              <ToggleInput
                isToggled={payInUSDTEnabled}
                onToggle={() => setPayInUSDTEnabled(!payInUSDTEnabled)}
                size="sm"
              />
            </div>
            <div className="w-[33%] flex justify-end">
              <ToggleInput
                isToggled={payOutUSDTEnabled}
                onToggle={() => setPayOutUSDTEnabled(!payOutUSDTEnabled)}
                size="sm"
              />
            </div>
          </div>
          <div className="flex flex-row justify-between items-center border-b border-border-subtle py-[8px] px-[8px]">
            <span className="font-medium text-[10px] w-[33%]">USDC</span>
            <div className="w-[33%] flex justify-end">
              <ToggleInput
                isToggled={payInUSDCEnabled}
                onToggle={() => setPayInUSDCEnabled(!payInUSDCEnabled)}
                size="sm"
              />
            </div>
            <div className="w-[33%] flex justify-end">
              <ToggleInput
                isToggled={payOutUSDCEnabled}
                onToggle={() => setPayOutUSDCEnabled(!payOutUSDCEnabled)}
                size="sm"
              />
            </div>
          </div>
          <div className="flex flex-row justify-between items-center py-[8px] px-[8px]">
            <span className="font-medium text-[10px] w-[33%]">DAI</span>
            <div className="w-[33%] flex justify-end">
              <ToggleInput
                isToggled={payInDAIEnabled}
                onToggle={() => setPayInDAIEnabled(!payInDAIEnabled)}
                size="sm"
              />
            </div>
            <div className="w-[33%] flex justify-end">
              <ToggleInput
                isToggled={payOutDAIEnabled}
                onToggle={() => setPayOutDAIEnabled(!payOutDAIEnabled)}
                size="sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Networks */}
      <div className="flex flex-col">
        <span className="text-[12px] font-semibold pb-[8px]">NETWORK</span>
        <div className="bg-bg-page-body border border-border-subtle rounded-[8px]">
          <div className="flex flex-row items-center border-b border-border-subtle py-[8px] px-[8px]">
            <span className="font-bold text-[10px] w-[33%] text-start">
              Network
            </span>
            <span className="font-bold text-[10px] w-[33%] text-end">
              PayIn
            </span>
            <span className="font-bold text-[10px] w-[33%] text-end">
              PayOut
            </span>
          </div>
          <div className="flex flex-row justify-between items-center border-b border-border-subtle py-[8px] px-[8px]">
            <span className="font-medium text-[10px] w-[33%]">ETH</span>

            <div className="w-[33%] flex justify-end">
              <ToggleInput
                isToggled={ethPayInNetworkEnabled}
                onToggle={() => setEthNetworkEnabled(!ethPayInNetworkEnabled)}
                size="sm"
              />
            </div>

            <div className="w-[33%] flex justify-end">
              <ToggleInput
                isToggled={ethPayOutNetworkEnabled}
                onToggle={() =>
                  setEthPayOutNetworkEnabled(!ethPayOutNetworkEnabled)
                }
                size="sm"
              />
            </div>
          </div>
          <div className="flex flex-row justify-between items-center border-b border-border-subtle py-[8px] px-[8px]">
            <span className="font-medium text-[10px] w-[33%]">POL</span>
            <div className="w-[33%] flex justify-end">
              <ToggleInput
                isToggled={polPayInNetworkEnabled}
                onToggle={() => setPolNetworkEnabled(!polPayInNetworkEnabled)}
                size="sm"
              />
            </div>
            <div className="w-[33%] flex justify-end">
              <ToggleInput
                isToggled={polPayOutNetworkEnabled}
                onToggle={() =>
                  setPolPayOutNetworkEnabled(!polPayOutNetworkEnabled)
                }
                size="sm"
              />
            </div>
          </div>
          <div className="flex flex-row justify-between items-center py-[8px] px-[8px]">
            <span className="font-medium text-[10px] w-[33%]">MATIC</span>
            <div className="w-[33%] flex justify-end">
              <ToggleInput
                isToggled={maticPayInNetworkEnabled}
                onToggle={() =>
                  setMaticNetworkEnabled(!maticPayInNetworkEnabled)
                }
                size="sm"
              />
            </div>
            <div className="w-[33%] flex justify-end">
              <ToggleInput
                isToggled={maticPayOutNetworkEnabled}
                onToggle={() =>
                  setMaticPayOutNetworkEnabled(!maticPayOutNetworkEnabled)
                }
                size="sm"
              />
            </div>
          </div>
          <div className="flex flex-row justify-between items-center py-[8px] px-[8px]">
            <span className="font-medium text-[10px] w-[33%]">BSC</span>
            <div className="w-[33%] flex justify-end">
              <ToggleInput
                isToggled={bscPayInNetworkEnabled}
                onToggle={() => setBscNetworkEnabled(!bscPayInNetworkEnabled)}
                size="sm"
              />
            </div>
            <div className="w-[33%] flex justify-end">
              <ToggleInput
                isToggled={bscPayOutNetworkEnabled}
                onToggle={() =>
                  setBscPayOutNetworkEnabled(!bscPayOutNetworkEnabled)
                }
                size="sm"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 bg-bg-base border-t border-border-subtle p-[12px]">
        {userManegementClasses.updateDeleteButton({
          onUpdate: handleUpdate,
          onCancel: handleCancel,
          isDirty: isDirty,
        })}
      </div>
    </div>
  );
};

export default CPGConfigContainer;
